import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import BpkBlockquote from '@skyscanner/backpack-web/bpk-component-blockquote';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import {
  BpkList,
  BpkListItem,
} from '@skyscanner/backpack-web/bpk-component-list';
import {
  BpkTable,
  BpkTableBody,
  BpkTableCell,
  BpkTableHeadCell,
  BpkTableRow,
} from '@skyscanner/backpack-web/bpk-component-table';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';

import type { Node, Document } from '@contentful/rich-text-types';

import STYLES from './RichText.module.scss';

export type RichTextProps = {
  body: Document;
};

// Inline utility functions
const isExternal = (url: URL): boolean =>
  !url.hostname.includes('skyscanner.') && !url.hostname.includes('tianxun.');

const isFunnel = (url: URL): boolean => {
  if (url.pathname.includes('/transport/flights/')) return true;
  if (url.pathname.includes('/carhire/results/')) return true;
  if (url.pathname.includes('/hotels/search')) return true;
  return false;
};

const getRel = (url: URL): string | null => {
  if (isExternal(url)) {
    return 'noopener noreferrer nofollow';
  }
  if (isFunnel(url)) {
    return 'nofollow';
  }
  return null;
};

// Renderer functions moved outside component
const renderHeading1 = (_node: Node, children: ReactNode) => (
  <BpkText tagName="h1" textStyle={TEXT_STYLES.heading2}>
    {children}
  </BpkText>
);

const renderHeading2 = (_node: Node, children: ReactNode) => (
  <BpkText tagName="h2" textStyle={TEXT_STYLES.heading3}>
    {children}
  </BpkText>
);

const renderHeading3 = (_node: Node, children: ReactNode) => (
  <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
    {children}
  </BpkText>
);

const renderHeading4 = (_node: Node, children: ReactNode) => (
  <BpkText tagName="h4" textStyle={TEXT_STYLES.heading5}>
    {children}
  </BpkText>
);

const renderHeading5 = (_node: Node, children: ReactNode) => (
  <BpkText tagName="h5" textStyle={TEXT_STYLES.heading5}>
    {children}
  </BpkText>
);

const renderParagraph = (_node: Node, children: ReactNode) => (
  <BpkText tagName="p" textStyle={TEXT_STYLES.base}>
    {children}
  </BpkText>
);

const renderUlList = (_node: Node, children: ReactNode) => (
  <BpkList>{children}</BpkList>
);

const renderOlList = (_node: Node, children: ReactNode) => (
  <BpkList ordered>{children}</BpkList>
);

const renderListItem = (_node: Node, children: ReactNode) => (
  <BpkListItem>{children}</BpkListItem>
);

const renderQuote = (_node: Node, children: ReactNode) => (
  <BpkBlockquote>{children}</BpkBlockquote>
);

const renderTable = (_node: Node, children: ReactNode) => (
  <BpkTable>
    <BpkTableBody>{children}</BpkTableBody>
  </BpkTable>
);

const renderTableRow = (_node: Node, children: ReactNode) => (
  <BpkTableRow>{children}</BpkTableRow>
);

const renderTableCell = (_node: Node, children: ReactNode) => (
  <BpkTableCell>{children}</BpkTableCell>
);

const renderTableHeaderCell = (_node: Node, children: ReactNode) => (
  <BpkTableHeadCell>{children}</BpkTableHeadCell>
);

const renderHyperlink = (node: Node, children: ReactNode) => {
  if (!node.data.uri) {
    return <>{children}</>;
  }
  if (node.data.uri.startsWith('#')) {
    return <BpkLink href={node.data.uri}>{children}</BpkLink>;
  }

  const url = new URL(node.data.uri);
  const blank = isExternal(url);
  const rel = getRel(url);

  return (
    <BpkLink href={url.href} blank={blank} rel={rel}>
      {children}
    </BpkLink>
  );
};

const RichText = ({ body }: RichTextProps) => {
  const options = useMemo(
    () => ({
      renderNode: {
        [BLOCKS.HEADING_1]: renderHeading1,
        [BLOCKS.HEADING_2]: renderHeading2,
        [BLOCKS.HEADING_3]: renderHeading3,
        [BLOCKS.HEADING_4]: renderHeading4,
        [BLOCKS.HEADING_5]: renderHeading5,
        [BLOCKS.PARAGRAPH]: renderParagraph,
        [BLOCKS.UL_LIST]: renderUlList,
        [BLOCKS.OL_LIST]: renderOlList,
        [BLOCKS.LIST_ITEM]: renderListItem,
        [BLOCKS.QUOTE]: renderQuote,
        [BLOCKS.TABLE]: renderTable,
        [BLOCKS.TABLE_ROW]: renderTableRow,
        [BLOCKS.TABLE_CELL]: renderTableCell,
        [BLOCKS.TABLE_HEADER_CELL]: renderTableHeaderCell,
        [INLINES.HYPERLINK]: renderHyperlink,
      },
    }),
    [],
  );

  const reactComponents = documentToReactComponents(body, options);

  return <div className={STYLES.RichTextBlock}>{reactComponents}</div>;
};

export default RichText;
